import { Megaphone, Rss } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/v2/badge";
import EmptyState from "@/components/ui/v2/empty-state";
import { SEGMENT_STATUS_CONFIG } from "@/features/segments/constants";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import type { SegmentCampaign, SegmentModule } from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";

const MAX_VISIBLE_JOB_BOARDS = 2;

type WhereUsedSectionProps = {
  campaigns: SegmentCampaign[];
  modules: SegmentModule[];
};

export function WhereUsedSection({ campaigns, modules }: WhereUsedSectionProps) {
  const { t } = useTranslation();
  const isEmpty = campaigns.length === 0 && modules.length === 0;
  const targetRef = useTourTarget<HTMLElement>(SEGMENT_TOUR_TARGET.OVERVIEW_WHERE_USED);

  if (isEmpty) {
    return (
      <section ref={targetRef}>
        <h3 className="mb-3 text-sm font-semibold">{t("admin.segments.whereUsed", "Where it's used")}</h3>
        <EmptyState
          layout="inline"
          icon={Megaphone}
          title={t("admin.segments.notUsedYet", "Not used yet")}
          description={t("admin.segments.notUsedYetDescription", "This segment is not used in any campaign or module yet.")}
        />
      </section>
    );
  }

  let itemIndex = 0;

  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold">{t("admin.segments.whereUsed", "Where it's used")}</h3>
      <div className="flex flex-col gap-2">
        {campaigns.map((campaign) => {
          const { labelKey, variant } = SEGMENT_STATUS_CONFIG[campaign.status];
          const visibleBoards = campaign.jobBoards.slice(0, MAX_VISIBLE_JOB_BOARDS);
          const remaining = campaign.jobBoards.length - MAX_VISIBLE_JOB_BOARDS;
          const index = itemIndex++;

          return (
            <motion.div
              key={campaign.id}
              ref={index === 0 ? targetRef : undefined}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: index * 0.04 }}
              data-slot="panel"
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15">
                <Megaphone className="size-4 text-emerald-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{campaign.name}</p>
                {campaign.jobBoards.length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    {visibleBoards.map((jb, idx) => (
                      <Badge key={`${jb.integrationName}-${idx}`} variant="outline" size="small">
                        {t(jb.integrationName, jb.integrationName)}
                      </Badge>
                    ))}
                    {remaining > 0 && (
                      <Badge variant="outline" size="small">+{remaining}</Badge>
                    )}
                  </div>
                )}
              </div>
              <Badge variant={variant} size="small">{t(labelKey)}</Badge>
            </motion.div>
          );
        })}

        {modules.map((mod) => {
          const index = itemIndex++;

          return (
            <motion.div
              key={mod.clientIntegrationModuleId}
              ref={index === 0 ? targetRef : undefined}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: index * 0.04 }}
              data-slot="panel"
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sky-500/15">
                <Rss className="size-4 text-sky-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {t(mod.moduleDisplayName, mod.moduleDisplayName)} {t(mod.integrationName, mod.integrationName)}
                </p>
              </div>
              <Badge variant={mod.isActive ? "success" : "neutral"} size="small">
                {t(mod.isActive ? "common.active" : "common.inactive")}
              </Badge>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
